import React, { useEffect, useState } from 'react';
import { animateScroll as scroll } from 'react-scroll/modules';

import ValutaFilter from '@eika-infrastruktur/ui-valuta-filter';
import { IconButton, StandardButton } from '@eika/button';
import { MarketCard } from '@eika/card';
import { IconTypes } from '@eika/icon';
import { Label, type LabelTypes } from '@eika/label';
import { Modal } from '@eika/modal';

import { StringKeyValue } from '../../../actions/types';
import StepButtons from '../../../components/Steps/StepButtons';
import { CalculatorReducer } from '../../../reducers/calculator';
import { CompletionReducer } from '../../../reducers/completion';
import { FeatureToggleReducer } from '../../../reducers/feature';
import { SblState } from '../../../reducers/sbl';
import { VehicleReducer } from '../../../reducers/vehicle';
import { formatDuration, formatRegNo } from '../../../util/common';
import { getProductCompletionIngress } from '../../../util/mappings';
import ApplicationStatus from '../../../util/mappings/applicationStatus';
import { TrackingStep, trackEvent } from '../../../util/piwik-utils';

interface Props {
    action: {
        openStep: (step: number) => void;
    };
    completion: CompletionReducer;
    calculator: CalculatorReducer;
    vehicle: VehicleReducer;
    text: StringKeyValue;
    feature: FeatureToggleReducer;
    sbl: SblState;
}

const LoanCompletionStart = (props: Props): JSX.Element => {
    useEffect(() => {
        scroll.scrollToTop();
    }, []);

    const [showTLModal, setShowTLModal] = useState(false);

    const { action, completion, calculator, vehicle, text, feature, sbl } = props;
    const { openStep } = action;
    const { ongoing } = completion;
    const { loanInfo } = calculator;
    const { applicationId, customerIsMainApplicant, trafficLight, applicationStatus } = ongoing;
    const manufacturer = vehicle.vehicleDetails?.manufacturer;
    const model = vehicle.vehicleDetails?.model;
    const manufactureYear = vehicle.vehicleDetails?.manufactureYear;

    const trafficLightValues: { [key: string]: { labelType: LabelTypes; label: string; modalText: string } } = {
        RED: {
            labelType: 'error',
            label: text.trafficLightRed,
            modalText: text.trafficLightModalP2Red,
        },
        YELLOW: {
            labelType: 'warning',
            label: text.trafficLightYellow,
            modalText: text.trafficLightModalP2Yellow,
        },
        GREEN: {
            labelType: 'success',
            label: text.trafficLightGreen,
            modalText: text.trafficLightModalP2Green,
        },
    };

    const {
        requestedLoanPeriodInMonths,
        vehicleRegistrationNumber,
        effectiveInterest,
        nominalInterest,
        vehiclePrice,
        invoiceFee,
        loanAmount,
        loanProduct,
        loanType,
        monthlyCost,
        pledgeFee,
        startupFee,
        totalCost,
    } = loanInfo;

    const {
        completionDetailsLoanInfoTitle,
        completionDetailsVehicleInfoTitle,
        completionDetailsCaseNo,
        completionDetailsEffectiveInterest,
        completionDetailsNominalInterest,
        completionDetailsLoanAmount,
        completionDetailsLoanPeriod,
        completionDetailsMonthlyCost,
        completionDetailsPledgeFee,
        completionDetailsStartupFee,
        completionDetailsTermFee,
        completionDetailsTotalCost,
        completionDetailsVehicleInfoManufacturer,
        completionDetailsVehicleInfoModel,
        completionDetailsVehicleInfoYear,
        completionDetailsVehicleInfoRegNo,
        completionDetailsVehicleInfoVehiclePrice,
        completionDetailsP1,
        completionDetailsP2,
        completionDetailsP3,
        completionDetailsP3CoApplicant,
        completionDetailsIngressNewCase,
    } = text;

    const completionDetailsIngress = getProductCompletionIngress(loanType, loanProduct, text);

    const handleNext = () => {
        trackEvent(TrackingStep.CompletionStart);
        if (feature.salgspantSkipSbl) {
            openStep(4);
            return;
        }
        if (sbl.sblStatus !== 'ACCEPTED') {
            openStep(2);
        } else openStep(3);
    };

    return (
        <div className="loan-completion-details">
            {applicationStatus === ApplicationStatus.newCase ? (
                <>
                    {completionDetailsIngressNewCase && (
                        <p className="text-center">{completionDetailsIngressNewCase}</p>
                    )}
                </>
            ) : (
                <>{completionDetailsIngress && <p className="text-center">{completionDetailsIngress}</p>}</>
            )}

            {/* Loan Info */}
            <MarketCard className="loan-completion-details__loan-info-card">
                <div className="custom-list">
                    <h2>{completionDetailsLoanInfoTitle}</h2>

                    {applicationId && (
                        <ul>
                            <li className="list-left">{completionDetailsCaseNo}</li>
                            <li className="list-right">{applicationId}</li>
                        </ul>
                    )}
                    {loanAmount && (
                        <ul>
                            <li className="list-left">{completionDetailsLoanAmount}</li>
                            <li className="list-right">{ValutaFilter(loanAmount, ' ', 0)} kr</li>
                        </ul>
                    )}
                    {requestedLoanPeriodInMonths && (
                        <ul>
                            <li className="list-left">{completionDetailsLoanPeriod}</li>
                            <li className="list-right">{formatDuration(requestedLoanPeriodInMonths)}</li>
                        </ul>
                    )}
                    {applicationStatus !== ApplicationStatus.newCase && (
                        <>
                            {startupFee && (
                                <ul>
                                    <li className="list-left">{completionDetailsStartupFee}</li>
                                    <li className="list-right">{ValutaFilter(startupFee, ' ', 0)} kr</li>
                                </ul>
                            )}
                            {pledgeFee && (
                                <ul>
                                    <li className="list-left">{completionDetailsPledgeFee}</li>
                                    <li className="list-right">{ValutaFilter(pledgeFee, ' ', 0)} kr</li>
                                </ul>
                            )}

                            {nominalInterest && (
                                <ul>
                                    <li className="list-left">{completionDetailsNominalInterest}</li>
                                    <li className="list-right">{nominalInterest} %</li>
                                </ul>
                            )}
                            {effectiveInterest && (
                                <ul>
                                    <li className="list-left">{completionDetailsEffectiveInterest}</li>
                                    <li className="list-right">{effectiveInterest} %</li>
                                </ul>
                            )}
                            {invoiceFee && (
                                <ul>
                                    <li className="list-left">{completionDetailsTermFee}</li>
                                    <li className="list-right">{ValutaFilter(invoiceFee, ' ', 0)} kr</li>
                                </ul>
                            )}
                            {monthlyCost && (
                                <ul>
                                    <li className="list-left">{completionDetailsMonthlyCost}</li>
                                    <li className="list-right">{ValutaFilter(monthlyCost, ' ', 0)} kr</li>
                                </ul>
                            )}
                            {totalCost && (
                                <ul>
                                    <li className="list-left">{completionDetailsTotalCost}</li>
                                    <li className="list-right">{ValutaFilter(totalCost, ' ', 0)} kr</li>
                                </ul>
                            )}
                        </>
                    )}

                    <h2>{completionDetailsVehicleInfoTitle}</h2>
                    {manufacturer && (
                        <ul>
                            <li className="list-left">{completionDetailsVehicleInfoManufacturer}</li>
                            <li className="list-right">{manufacturer}</li>
                        </ul>
                    )}
                    {model && (
                        <ul>
                            <li className="list-left">{completionDetailsVehicleInfoModel}</li>
                            <li className="list-right">{model}</li>
                        </ul>
                    )}
                    {manufactureYear && (
                        <ul>
                            <li className="list-left">{completionDetailsVehicleInfoYear}</li>
                            <li className="list-right">{manufactureYear}</li>
                        </ul>
                    )}
                    {vehicleRegistrationNumber && (
                        <ul>
                            <li className="list-left">{completionDetailsVehicleInfoRegNo}</li>
                            <li className="list-right">{formatRegNo(vehicleRegistrationNumber)}</li>
                        </ul>
                    )}
                    {vehiclePrice && (
                        <ul>
                            <li className="list-left">{completionDetailsVehicleInfoVehiclePrice}</li>
                            <li className="list-right">{ValutaFilter(vehiclePrice, ' ', 0)} kr</li>
                        </ul>
                    )}
                </div>
            </MarketCard>

            {applicationStatus !== ApplicationStatus.newCase && (
                <div className="loan-completion-details__text">
                    {completionDetailsP1 && <p>{completionDetailsP1}</p>}
                    {completionDetailsP2 && <p>{completionDetailsP2}</p>}
                    {customerIsMainApplicant ? (
                        <>{completionDetailsP3 && <p>{completionDetailsP3}</p>}</>
                    ) : (
                        <>{completionDetailsP3CoApplicant && <p>{completionDetailsP3CoApplicant}</p>}</>
                    )}
                </div>
            )}

            <StepButtons nextChangeEvent={() => handleNext()} />

            {trafficLight && feature.salgspantTrafficLight && (
                <>
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
                        <Label
                            type={trafficLightValues[trafficLight].labelType}
                            text={`${text.trafficLightPre} ${trafficLightValues[trafficLight].label}`}
                        />

                        <IconButton
                            screenReaderText="Informasjon om trafikklys"
                            icon={IconTypes.SPOERSMAALSTEGN_36}
                            onClick={() => setShowTLModal(true)}
                        />
                    </div>
                    <Modal
                        id="modal_trafficlight"
                        onClose={() => setShowTLModal(false)}
                        title={text.trafficLightModalTitle}
                        open={showTLModal}
                        appNamespace="no-eika-kredittbank"
                        setOpen={(open) => setShowTLModal(open)}
                    >
                        {text.trafficLightModalP1 && <p>{text.trafficLightModalP1}</p>}
                        {trafficLight && <p>{trafficLightValues[trafficLight].modalText}</p>}

                        <div className="consent-item__close-modal">
                            <StandardButton onClick={() => setShowTLModal(false)}>
                                {text.trafficLightModalClose}
                            </StandardButton>
                        </div>
                    </Modal>
                </>
            )}
        </div>
    );
};

export default LoanCompletionStart;
